import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatBRL, initials, resolveAvatar } from "@/lib/api";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { RealtimeProvider, useRealtime } from "@/contexts/RealtimeContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trophy, TrendingUp, X } from "lucide-react";
import confetti from "canvas-confetti";
import SprintOverlay from "@/components/SprintOverlay";
import CelebrationOverlay from "@/components/CelebrationOverlay";

const CEAP_LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_dashboard-ceap/artifacts/b4qjckp1_nova_logo_CEAP_2020-removebg-preview.png";

function TVContent() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [guerra, setGuerra] = useState(null);
  const [banner, setBanner] = useState(null);
  const [nucleos, setNucleos] = useState([]);
  const [nucleoId, setNucleoId] = useState(() => localStorage.getItem("tv_nucleo") || "");
  const { lastEvent } = useRealtime() || {};

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "diretoria") { nav("/funil"); return; }
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user || user.role !== "diretoria") return;
    api.get("/nucleos").then((r) => {
      setNucleos(r.data || []);
      if (!nucleoId && r.data?.[0]) {
        setNucleoId(r.data[0].id);
        localStorage.setItem("tv_nucleo", r.data[0].id);
      }
    }).catch(() => {});
  }, [user, nucleoId]);

  const load = async () => {
    if (!nucleoId) return;
    try {
      const r = await api.get(`/dashboards/tv?nucleo_id=${nucleoId}`);
      setData(r.data);
    } catch (err) { console.warn("TV dash load failed:", err?.message || err); }
  };
  const loadGuerra = async () => {
    if (!nucleoId) return;
    try {
      const r = await api.get(`/dashboards/tv-guerra?nucleo_id=${nucleoId}`);
      setGuerra(r.data);
    } catch (err) { console.warn("TV guerra load failed:", err?.message || err); }
  };

  useEffect(() => {
    if (!nucleoId) return;
    load(); loadGuerra();
    const t = setInterval(() => { load(); loadGuerra(); }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [nucleoId]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === "inscricao") {
      // Só celebra se for do núcleo assistido
      if (nucleoId && lastEvent.nucleo_id && lastEvent.nucleo_id !== nucleoId) return;
      setBanner(lastEvent);
      confetti({ particleCount: 400, spread: 160, origin: { y: 0.5 } });
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const now = ctx.currentTime;
        [880, 1320, 1760, 440].forEach((f, i) => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.type = "sine"; o.frequency.value = f;
          g.gain.setValueAtTime(0.0001, now);
          g.gain.exponentialRampToValueAtTime(0.35 / (i + 1), now + 0.005);
          g.gain.exponentialRampToValueAtTime(0.0001, now + 1.5);
          o.connect(g).connect(ctx.destination); o.start(now); o.stop(now + 1.6);
        });
      } catch (err) { console.warn("TV audio ctx failed:", err?.message || err); }
      load(); loadGuerra();
      setTimeout(() => setBanner(null), 8000);
    }
    // eslint-disable-next-line
  }, [lastEvent]);

  const trocarNucleo = (v) => {
    setNucleoId(v);
    localStorage.setItem("tv_nucleo", v);
  };

  if (loading || !user) return <div className="tv-bg flex items-center justify-center text-3xl">Carregando...</div>;
  if (user.role !== "diretoria") return null;
  if (!data) return <div className="tv-bg flex items-center justify-center text-3xl">Carregando painel...</div>;

  const pct = data.percentual;

  return (
    <div className="tv-bg p-8 md:p-12" data-testid="tv-mode">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center p-2">
            <img src={CEAP_LOGO} alt="CEAP" className="max-w-full max-h-full object-contain" />
          </div>
          <div>
            <div className="font-display text-2xl md:text-4xl font-extrabold tracking-tight">PAINEL DE VENDAS AO VIVO</div>
            <div className="text-white/60 text-sm md:text-base">
              {data.nucleo_nome ? `Núcleo ${data.nucleo_nome} · ` : ""}{data.mes_ano}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={nucleoId} onValueChange={trocarNucleo}>
            <SelectTrigger className="w-48 bg-white/10 text-white border-white/20" data-testid="tv-select-nucleo">
              <SelectValue placeholder="Núcleo" />
            </SelectTrigger>
            <SelectContent>
              {nucleos.map((n) => (
                <SelectItem key={n.id} value={n.id}>{n.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-right">
            <div className="text-white/60 text-xs uppercase tracking-widest">Agora</div>
            <div className="font-display text-2xl tabular-nums" id="tv-clock">
              {new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          <button
            onClick={() => window.close() || nav("/funil")}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"
            title="Sair do modo TV"
            data-testid="tv-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Thermometer / big number */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white/5 backdrop-blur rounded-3xl p-8 border border-white/10">
            <div className="text-white/60 text-sm uppercase tracking-widest">Realizado / Meta</div>
            <div className="font-display text-6xl md:text-8xl font-extrabold tabular-nums mt-2">
              {formatBRL(data.realizado)}
            </div>
            <div className="text-2xl md:text-3xl text-white/70 mt-1">de {formatBRL(data.meta)}</div>
            <div className="mt-8 relative">
              <div className="h-8 rounded-full bg-white/10 overflow-hidden">
                <div
                  className={`h-full rounded-full ${pct >= 100 ? "bg-gradient-to-r from-emerald-400 to-emerald-600" : "bg-gradient-to-r from-blue-400 to-indigo-500"}`}
                  style={{ width: `${Math.min(100, pct)}%`, transition: "width 1.5s ease-out" }}
                ></div>
              </div>
              <div className="mt-3 flex justify-between text-white/60 text-sm">
                <span>0</span>
                <span className="font-display text-2xl font-bold text-white">{pct.toFixed(1)}%</span>
                <span>{formatBRL(data.meta)}</span>
              </div>
            </div>
          </div>

          {/* Podium */}
          {data.ranking.length > 0 && (
            <div className="bg-white/5 backdrop-blur rounded-3xl p-6 border border-white/10">
              <div className="text-white/60 text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5" /> Pódio de Vendas
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[1, 0, 2].map((i, idx) => {
                  const item = data.ranking[i];
                  if (!item) return <div key={idx}></div>;
                  const colors = ["from-slate-300 to-slate-500", "from-yellow-400 to-amber-600", "from-orange-500 to-orange-700"];
                  const heights = ["h-32", "h-44", "h-24"];
                  return (
                    <div key={idx} className="flex flex-col items-center">
                      <Avatar className="w-20 h-20 ring-4 ring-white/20 mb-2">
                        <AvatarImage src={resolveAvatar(item.avatar_url)} alt={item.nome} />
                        <AvatarFallback className={`bg-gradient-to-br ${colors[idx]} text-white text-2xl font-bold`}>
                          {initials(item.nome)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-lg font-semibold text-center line-clamp-1">{item.nome}</div>
                      <div className="text-white/60 text-sm mb-2">{formatBRL(item.valor)}</div>
                      <div className={`w-full ${heights[idx]} rounded-t-2xl bg-gradient-to-b ${colors[idx]} flex items-start justify-center pt-2 font-display font-extrabold text-4xl`}>
                        {i + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right: leaderboard */}
        <div className="bg-white/5 backdrop-blur rounded-3xl p-6 border border-white/10">
          <div className="text-white/60 text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Ranking Completo
          </div>
          <div className="space-y-2">
            {data.ranking.map((r, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${i < 3 ? "bg-white/10" : ""}`}>
                <span className="text-2xl font-display font-bold tabular-nums w-8 text-white/80">{i + 1}</span>
                <Avatar className="w-10 h-10">
                  <AvatarImage src={resolveAvatar(r.avatar_url)} alt={r.nome} />
                  <AvatarFallback className="bg-white/10 text-white font-bold">{initials(r.nome)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.nome}</div>
                  <div className="text-white/60 text-xs tabular-nums">{formatBRL(r.valor)}</div>
                </div>
              </div>
            ))}
            {data.ranking.length === 0 && <div className="text-white/40 text-center py-8">Aguardando primeiras vendas do mês...</div>}
          </div>
        </div>
      </div>

      {/* Dash Guerra completo */}
      {guerra && guerra.linhas && guerra.linhas.length > 0 && (
        <div className="mt-8 bg-white/5 backdrop-blur rounded-3xl p-6 border border-white/10">
          <div className="text-white/60 text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Guerra ao Vivo — Vendedoras do Núcleo
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-white/50 border-b border-white/10">
                  <th className="pb-2 pr-3">Vendedora</th>
                  <th className="pb-2 pr-3 text-right">Prosp.</th>
                  <th className="pb-2 pr-3 text-right">Lig.</th>
                  <th className="pb-2 pr-3 text-right">WA</th>
                  <th className="pb-2 pr-3 text-right">Inscr.</th>
                  <th className="pb-2 pr-3 text-right">Valor</th>
                  <th className="pb-2 pr-3 text-right">Ticket</th>
                  <th className="pb-2 text-right">% Meta</th>
                </tr>
              </thead>
              <tbody>
                {guerra.linhas.map((l) => (
                  <tr key={l.vendedora_id} className={`border-b border-white/5 ${l.percentual_meta >= 100 ? "bg-emerald-500/10" : ""}`}>
                    <td className="py-2 pr-3 font-semibold">{l.nome}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.prospectados}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.ligacoes}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.whatsapps}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-bold">{l.inscritos}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-bold">{formatBRL(l.valor_vendido)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{formatBRL(l.ticket_medio)}</td>
                    <td className={`py-2 text-right font-bold tabular-nums ${l.percentual_meta >= 100 ? "text-emerald-300" : l.percentual_meta >= 70 ? "text-amber-300" : "text-red-300"}`}>
                      {l.percentual_meta.toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Celebration banner */}
      {banner && (
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
          <div className="celebrate-anim bg-gradient-to-r from-emerald-500 to-blue-600 rounded-3xl px-16 py-12 shadow-2xl border-4 border-white/20 max-w-4xl mx-8">
            <div className="text-white/80 text-lg font-bold uppercase tracking-widest">🎉 Nova venda!</div>
            <div className="font-display text-6xl md:text-8xl font-extrabold text-white mt-2 leading-none">
              {banner.vendedora_nome}
            </div>
            <div className="font-display text-4xl md:text-5xl font-bold text-white/95 mt-2">
              {banner.quantidade} {banner.quantidade === 1 ? "inscrição" : "inscrições"} em {banner.municipio_nome}
            </div>
            <div className="font-display text-5xl md:text-7xl font-extrabold text-white mt-3 tabular-nums">
              {formatBRL(banner.valor)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TVMode() {
  return (
    <AuthProvider>
      <RealtimeProvider>
        <TVContent />
        <SprintOverlay />
        <CelebrationOverlay />
      </RealtimeProvider>
    </AuthProvider>
  );
}
